import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { OoCaseSidebarContext } from 'components/CaseSidebar/buildOoCaseSidebar';
import { FormattedNote } from 'components/common';
import type { ApplicationDetailsFormatted } from 'queries/applications/useApplicationByReferenceNumber/types';
import { useBtlApplicationInProgressById, useConveyancing } from 'queries/btlApplications';
import { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useSnackbar } from 'notistack';
import { notesApi } from 'api/notes';
import { useCanAccessHppPages } from 'hooks';
import {
  useIsOoCaseDipAcceptedEditEnabled,
  useIsOoCaseNewSectionsEnabled,
  useIsOoCaseSolicitorsEnabled,
} from 'providers/PosthogProvider';

export function useOoApplicationViewData(application: ApplicationDetailsFormatted) {
  const caseId = String(application.id);
  const intl = useIntl();
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  const { data: conveyancing, isLoading: isConveyancingLoading } = useConveyancing(caseId);
  const { data: caseData, isLoading: isCaseLoading } = useBtlApplicationInProgressById(caseId);
  const isNewSectionsEnabled = useIsOoCaseNewSectionsEnabled();
  const canAccessSolicitorsPage = useIsOoCaseSolicitorsEnabled();
  const canEditDipAcceptedSections = useIsOoCaseDipAcceptedEditEnabled();
  const { canAccess: canAccessHppPages } = useCanAccessHppPages();

  const bankAccount = caseData?.borrower?.bankAccount;
  const valuation = application.valuation;
  const isCaseInitialLoading = isCaseLoading && !caseData;

  const sidebarContext = useMemo<OoCaseSidebarContext>(
    () => ({
      status: application.status,
      hasValuation: Boolean(valuation),
      hasConveyancing: Boolean(conveyancing),
      isNewSectionsEnabled,
      canAccessHppPages,
      canAccessSolicitorsPage,
      canEditDipAcceptedSections,
    }),
    [
      application.status,
      conveyancing,
      isNewSectionsEnabled,
      valuation,
      canAccessHppPages,
      canAccessSolicitorsPage,
      canEditDipAcceptedSections,
    ],
  );

  const formattedNotes: FormattedNote[] = useMemo(() => {
    if (!application.notes) {
      return [];
    }

    return application.notes.map(note => ({
      id: note.id,
      title: note.title,
      content: note.content,
      createdAt: note.createdAt,
      creator: note.createdBy,
    }));
  }, [application.notes]);

  const { mutateAsync: createNote, isLoading: isCreatingNote } = useMutation(
    ({ title, content }: { title: string; content: string }) =>
      notesApi.createBtlNote(caseId, { title, content, isInternal: false }),
    {
      onSuccess: () => {
        enqueueSnackbar(intl.formatMessage({ id: 'applicationDetails.note.create.success' }), { variant: 'success' });
        queryClient.invalidateQueries({ queryKey: ['analystApplications'] });
      },
      onError: (error: Error) => {
        enqueueSnackbar(error.message || 'Failed to create note', {
          variant: 'error',
        });
      },
    },
  );

  const handleCreateNote = useCallback(
    async (title: string, content: string) => {
      await createNote({ title, content });
    },
    [createNote],
  );

  return useMemo(
    () => ({
      caseId,
      case: caseData,
      valuation,
      applicationFilesInternal: application.files?.internal,
      isBankDetailsComplete: Boolean(bankAccount?.accountName),
      bankAccount,
      isCaseLoading: isCaseInitialLoading,
      isConveyancingLoading,
      conveyancing,
      sidebarContext,
      formattedNotes,
      onCreateNote: handleCreateNote,
      isCreatingNote,
    }),
    [
      application.files?.internal,
      bankAccount,
      caseData,
      caseId,
      conveyancing,
      formattedNotes,
      handleCreateNote,
      isCaseInitialLoading,
      isConveyancingLoading,
      isCreatingNote,
      sidebarContext,
      valuation,
    ],
  );
}
